import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import {
  type TaxPrepOrganizerViewModel,
  OrganizerStep,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/TaxPrepOrganizerViewModel'
import { TaxpayerAccordion } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/TaxpayerAccordion'
import { SpouseAccordion } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/SpouseAccordion'
import { DependentAccordion } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/DependentAccordion'
import { Id } from '@/infra/id'
import { RemoveDependentDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/RemoveDependentDialog'
import { StepCardListContainer } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/StepCardListContainer'

interface PersonalStepCardListContainerProps {
  vm: TaxPrepOrganizerViewModel
  testId?: string
}

export const PersonalStepCardListContainer = observer(
  function PersonalStepCardListContainer({
    vm,
    testId,
  }: PersonalStepCardListContainerProps) {
    return (
      <>
        <StepCardListContainer
          title="You and your household information"
          actions={
            <ButtonGroup>
              <Button
                label="Add dependent"
                variant="outline"
                onPress={vm.addDependentDialogVm.showDialog}
                isSkeleton={vm.isLoading}
                testId={testId ? `${testId}-add-dependent-button` : undefined}
              />
              <Button
                iconLeft="tick"
                label="Confirm"
                isPending={vm.save.pending}
                onPress={() =>
                  vm.save({ type: 'step', step: OrganizerStep.Business })
                }
                isSkeleton={vm.isLoading}
                testId={testId ? `${testId}-confirm-button` : undefined}
              />
            </ButtonGroup>
          }
          hasError={vm.showValidationError}
          isSkeleton={vm.isLoading}
          testId={testId}
        >
          <TaxpayerAccordion
            vm={vm.taxpayerVm}
            testId={testId ? `${testId}-taxpayer-accordion` : undefined}
          />
          {vm.showSpouse && (
            <SpouseAccordion
              vm={vm.spouseVm}
              onSetAsTaxpayer={vm.swapTaxpayerAndSpouse}
              testId={testId ? `${testId}-spouse-accordion` : undefined}
            />
          )}
          {vm.dependentVms.map((depVm, index) => (
            <DependentAccordion
              key={depVm.id || index}
              vm={depVm}
              onRemove={() =>
                vm.removeDependentDialogVm.showDialog({
                  id: Id.assert(depVm.id),
                  displayName: depVm.fullName,
                })
              }
              testId={
                testId ? `${testId}-dependent-accordion-${index}` : undefined
              }
            />
          ))}
        </StepCardListContainer>
        <RemoveDependentDialog
          vm={vm.removeDependentDialogVm}
          testId={testId ? `${testId}-remove-dependent-dialog` : undefined}
        />
      </>
    )
  },
)
